import { Component, Inject, Injector, Input, AfterViewInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseComponent } from 'projects/kiosk-app/src/app/components';
import { TemplateSetting } from 'projects/kiosk-app/src/app/models/template';
import { StyleService } from 'projects/kiosk-app/src/app/services';

type RedeemTypeV2 = 'freePlay' | 'matchPlay' | 'directBet' | 'cashValue' | 'dining';

export type RedeemOptionUI = {
  id: string;
  type: RedeemTypeV2;
  name: string;
  imageLink?: string | null;
};


type TabId = 'all' | RedeemTypeV2;

type RedeemTab = {
  id: TabId;
  label: string;
  count?: number;
};

export type RedeemPickerData = {
  options: RedeemOptionUI[];
  selectedId?: string;
  groupByType?: boolean;
  noOptionsMessage?: string;
  tab?: TemplateSetting; 
  mainTemplate?: any; 
};

@Component({
  selector: 'app-redemption-v2-modal',
  templateUrl: './redemption-v2-modal.component.html',
  styleUrls: ['./redemption-v2-modal.component.scss'],
})
export class RedemptionV2ModalComponent extends BaseComponent implements AfterViewInit, OnDestroy {

  public options: RedeemOptionUI[] = [];
  public pages: RedeemOptionUI[][] = [];
  public filteredOptions: RedeemOptionUI[] = [];

  public tabs: RedeemTab[] = [];
  public activeTab: TabId = 'all';
  public pageIndex = 0;
  public noOptionsMessage = 'No redemption option currently available.';

  public styleService: StyleService;

  // Swipe handling properties
  private touchStartX = 0;
  private touchEndX = 0;
  private touchStartY = 0;
  private touchEndY = 0;
  private minSwipeDistance = 50;
  private gridElement: HTMLElement | null = null;

  constructor(
    protected override readonly injector: Injector,
    private dialogRef: MatDialogRef<RedemptionV2ModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RedeemPickerData
  ) {
    super(injector);
    this.options = (data?.options ?? []).filter(o => !!o);
    this.styleService = this.injector.get(StyleService);
    
    if (data?.noOptionsMessage) this.noOptionsMessage = data.noOptionsMessage;

    const selected = this.options.find(o => o.id === data?.selectedId);
    if (selected) {
      this.activeTab = data?.groupByType ? selected.type : 'all';
    }

    this.buildTabs(!!data?.groupByType);
    this.applyFilter();
  }

  ngAfterViewInit(): void {
    // Set up swipe listeners
    this.gridElement = document.querySelector('.page-grid');
    
    if (this.gridElement) {
      this.gridElement.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: true });
      this.gridElement.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
      this.gridElement.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: true });
    }
  }

  override ngOnDestroy(): void {
    // Clean up swipe listeners
    if (this.gridElement) {
      this.gridElement.removeEventListener('touchstart', this.handleTouchStart.bind(this));
      this.gridElement.removeEventListener('touchmove', this.handleTouchMove.bind(this));
      this.gridElement.removeEventListener('touchend', this.handleTouchEnd.bind(this));
    }
  }

  // Swipe handling methods
  private handleTouchStart(event: TouchEvent): void {
    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
  }

  private handleTouchMove(event: TouchEvent): void {
    const touchCurrentX = event.touches[0].clientX;
    const touchCurrentY = event.touches[0].clientY;
    
    const deltaX = Math.abs(touchCurrentX - this.touchStartX);
    const deltaY = Math.abs(touchCurrentY - this.touchStartY);
    
    // Prevent default scroll only for horizontal swipes
    if (deltaX > deltaY && deltaX > 10) {
      event.preventDefault();
    }
  }

  private handleTouchEnd(event: TouchEvent): void {
    this.touchEndX = event.changedTouches[0].clientX;
    this.touchEndY = event.changedTouches[0].clientY;
    
    this.handleSwipe();
  }

  private handleSwipe(): void {
    const deltaX = this.touchEndX - this.touchStartX;
    const deltaY = this.touchEndY - this.touchStartY;
    
    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    
    if (isHorizontalSwipe && Math.abs(deltaX) > this.minSwipeDistance) {
      if (deltaX > 0) {
        // Swiped right - go to previous page
        this.prevPage();
      } else {
        // Swiped left - go to next page
        this.nextPage();
      }
    }
  }

  public close(): void {
    this.dialogRef.close(null);
  }

  public closeModal(): void {
    this.dialogRef.close();
  }

  public getTabButtonStyles(): any {
    return this.styleService.getStyleRule(
      [this.data?.tab?.configurations?.modal?.tabButtonRedeemRisk?.text, null, 'heading6'],
      [this.data?.tab?.configurations?.modal?.tabButtonRedeemRisk?.background, null],
      [this.data?.tab?.configurations?.modal?.tabButtonRedeemRisk?.border]
    );
  }

  public getArrowStyles(dir: 'prev' | 'next'): Record<string, any> {
    const cfg = this.data?.tab?.configurations?.modal?.arrowButton;

    const size = this.styleService.isQt3Kiosk
      ? Number(cfg?.sizeQt3 ?? cfg?.size ?? 48)
      : Number(cfg?.size ?? 36);

    const color = cfg?.background?.color || '#2e9c99';

    const halfHeight = Math.round(size * 0.6);  
    const width = Math.round(size * 0.45);    

    const s: Record<string, any> = {
      width: '0',
      height: '0',
      borderTop: `${halfHeight}px solid transparent`,
      borderBottom: `${halfHeight}px solid transparent`,
      background: 'transparent',
      cursor: 'pointer',
      outline: 'none',
      boxShadow: 'none',
      filter: 'none',
    };

    if (dir === 'prev') s.borderRight = `${width}px solid ${color}`;
    else s.borderLeft = `${width}px solid ${color}`;

    return s;
  }

  public get cfg(): any {
    return (this.data?.tab as any)?.configurations;
  }

  public select(opt: RedeemOptionUI): void {
    this.dialogRef.close(opt);
  }

  public setTab(tabId: TabId): void {
    this.activeTab = tabId;
    this.applyFilter();
  }

  private rebuildPages(): void {
    const list = this.filteredOptions;
    const pageSize = 12; // 4 cols * 3 rows
    const pages: RedeemOptionUI[][] = [];

    for (let i = 0; i < list.length; i += pageSize) {
      pages.push(list.slice(i, i + pageSize));
    }

    this.pages = pages.length ? pages : [[]];
    this.pageIndex = 0;
  }

  public prevPage(): void {
    if (this.pageIndex <= 0) return;
    this.pageIndex--;
  }

  public nextPage(): void {
    if (this.pageIndex >= this.pages.length - 1) return;
    this.pageIndex++;
  }

  private buildTabs(groupByType: boolean): void {
    if (!groupByType) {
      this.tabs = [];
      this.activeTab = 'all';
      return;
    }

    const counts = this.options.reduce((acc, o) => {
      acc[o.type] = (acc[o.type] ?? 0) + 1;
      return acc;
    }, {} as Record<RedeemTypeV2, number>);

    const order: RedeemTypeV2[] = ['freePlay', 'directBet', 'matchPlay', 'dining', 'cashValue'];

    this.tabs = [
      { id: 'all', label: 'All', count: this.options.length },
      ...order
        .filter(t => (counts[t] ?? 0) > 0)
        .map(t => ({ id: t, label: this.tabLabel(t), count: counts[t] })),
    ];

    const valid = this.tabs.some(t => t.id === this.activeTab);
    this.activeTab = valid ? this.activeTab : 'all';
  }

  private applyFilter(): void {
    if (this.activeTab === 'all') {
      this.filteredOptions = [...this.options];
    } else {
      this.filteredOptions = this.options.filter(o => o.type === this.activeTab);
    }

    this.rebuildPages();
  }

  private tabLabel(t: RedeemTypeV2): string {
    switch (t) {
      case 'freePlay': return 'Free Slot Play';
      case 'directBet': return 'Direct Bet';
      case 'matchPlay': return 'Match Play';
      case 'dining': return 'Dining';
      case 'cashValue': return 'Cash';
    }
  }
  
  public trackById(_: number, item: RedeemOptionUI): string {
    return item.id;
  }
}